import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Phone, Mail, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

const STATUSES = ["Yeni Başvuru", "İnceleniyor", "İletişime Geçildi", "Onaylandı", "Reddedildi"];
const STATUS_COLORS = {
  "Yeni Başvuru": "bg-[#1677FF]/10 text-[#1677FF]",
  "İnceleniyor": "bg-amber-100 text-amber-700",
  "İletişime Geçildi": "bg-[#22C7D6]/15 text-[#0B3B75]",
  "Onaylandı": "bg-emerald-100 text-emerald-700",
  "Reddedildi": "bg-red-100 text-red-600",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      await api.get("/auth/me");
      const [a, s] = await Promise.all([
        api.get("/partner-applications"),
        api.get("/partner-applications/stats"),
      ]);
      setApps(a.data);
      setStats(s.data);
    } catch {
      nav("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/partner-applications/${id}`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Durum güncellendi");
      load();
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const filtered = apps.filter((a) => {
    const t = `${a.full_name} ${a.province} ${a.email} ${a.phone}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7FAFC]"><Loader2 className="animate-spin text-[#1677FF]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F7FAFC]">
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-[#172033] hover:border-[#1677FF]">
            <LogOut size={16} /> Çıkış
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-[#172033]">Partner Başvuruları</h1>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <p className="text-sm text-[#667085]">Toplam</p>
            <p data-testid="stat-total" className="mt-1 font-heading text-3xl font-bold text-[#172033]">{stats?.total ?? 0}</p>
          </div>
          {STATUSES.map((s) => (
            <div key={s} className="rounded-2xl border border-gray-100 bg-white p-5">
              <p className="text-sm text-[#667085] truncate">{s}</p>
              <p className="mt-1 font-heading text-3xl font-bold text-[#172033]">{stats?.by_status?.[s] ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="mt-8 relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
          <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: isim, şehir, e-posta..." className="h-11 rounded-xl pl-9" />
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-gray-100 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#F7FAFC] text-[#667085]">
                <tr>
                  <th className="px-5 py-4 font-semibold">Ad Soyad</th>
                  <th className="px-5 py-4 font-semibold">İletişim</th>
                  <th className="px-5 py-4 font-semibold">Bölge</th>
                  <th className="px-5 py-4 font-semibold">Tür</th>
                  <th className="px-5 py-4 font-semibold">Tarih</th>
                  <th className="px-5 py-4 font-semibold">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-[#98A2B3]">Başvuru bulunamadı.</td></tr>
                )}
                {filtered.map((a) => (
                  <tr key={a.id} data-testid={`app-row-${a.id}`} className="hover:bg-[#F7FAFC]/60 cursor-pointer" onClick={() => setDetail(a)}>
                    <td className="px-5 py-4 font-semibold text-[#172033]">{a.full_name}</td>
                    <td className="px-5 py-4 text-[#475467]">
                      <div className="flex items-center gap-1.5"><Phone size={13} /> {a.phone}</div>
                      <div className="flex items-center gap-1.5 text-xs text-[#98A2B3]"><Mail size={12} /> {a.email}</div>
                    </td>
                    <td className="px-5 py-4 text-[#475467]">
                      <div className="flex items-center gap-1.5"><MapPin size={13} /> {a.province}</div>
                      <div className="text-xs text-[#98A2B3]">{a.all_districts ? "Tüm ilçeler" : `${a.districts.length} ilçe`}</div>
                    </td>
                    <td className="px-5 py-4 text-[#475467]">{a.business_type}</td>
                    <td className="px-5 py-4 text-[#98A2B3]">{new Date(a.created_at).toLocaleDateString("tr-TR")}</td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                        <SelectTrigger data-testid={`status-select-${a.id}`} className={`h-9 w-[160px] rounded-full border-0 text-xs font-semibold ${STATUS_COLORS[a.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.full_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <Row k="Telefon" v={detail.phone} />
              <Row k="E-posta" v={detail.email} />
              <Row k="Şehir" v={detail.province} />
              <Row k="İlçeler" v={detail.all_districts ? "Tüm ilçeler" : detail.districts.join(", ")} />
              <Row k="İşletme Türü" v={detail.business_type} />
              <Row k="Yerinde Hizmet" v={detail.mobile_service} />
              <Row k="Hizmetler" v={detail.services.join(", ") + (detail.other_service ? ` (${detail.other_service})` : "")} />
              <Row k="Deneyim" v={detail.experience} />
              <Row k="Su/Elektrik" v={detail.water_electricity} />
              <Row k="Günlük Kapasite" v={detail.daily_capacity} />
              <Row k="Ödeme" v={detail.payment_methods.join(", ")} />
              <Row k="Profil" v={detail.profile_url || "-"} />
              <Row k="Durum" v={detail.status} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex gap-3">
    <span className="w-32 shrink-0 font-semibold text-[#667085]">{k}</span>
    <span className="text-[#172033] break-words">{v}</span>
  </div>
);
